import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

interface AnalyticsData {
  event_name: string;
  count: number;
}

const AnalyticsDashboard = () => {
  const [topEvents, setTopEvents] = useState<AnalyticsData[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const { data, error } = await supabase
        .from('analytics_events')
        .select('event_name')
        .gte('created_at', new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString());

      if (error) throw error;

      // Count events
      const eventCounts = (data || []).reduce((acc, { event_name }) => {
        acc[event_name] = (acc[event_name] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);

      const chartData = Object.entries(eventCounts)
        .map(([event_name, count]) => ({ event_name, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 10);

      setTopEvents(chartData);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      toast.error('Failed to load analytics');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Loading...</CardTitle>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Analytics Overview</CardTitle>
        <CardDescription>
          Top 10 events in the last 30 days
        </CardDescription>
      </CardHeader>
      <CardContent>
        {topEvents.length === 0 ? (
          <p className="text-center text-muted-foreground py-8">
            No analytics data available
          </p>
        ) : (
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={topEvents}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="event_name" 
                angle={-45} 
                textAnchor="end" 
                height={100}
                fontSize={12}
              />
              <YAxis />
              <Tooltip />
              <Bar dataKey="count" fill="hsl(var(--primary))" />
            </BarChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
};

export default AnalyticsDashboard;
